function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { conformanceSelector, enabledSelector, labelSelector, newPathSelector, optionsSelector, valueSelector } from '../selectors/index';
import entityComposition from '../../common/classes/entityComposition';
import { UncomposedOptionPickerField } from './optionPickerField';
import logValueOnChange from '../commonActions/logValueOnChange';

/**
 * @class IssueSummaryPicker
 */
const IssueSummaryPicker = ({
  enabled,
  job,
  onChange,
  reportWithNDT: {
    list: ndtReports,
    indexQuery: ndtIndexQuery
  },
  reportWithNDTActions,
  reportWithMaintenance: {
    list: maintenanceReports,
    indexQuery: maintenanceIndexQuery
  },
  reportWithMaintenanceActions,
  summaryType: _summaryType = 'ndt',
  value: _value = null,
  allowsParentSelection: _allowsParentSelection = false,
  allowsMultipleSelection: _allowsMultipleSelection = false,
  allowsOther: _allowsOther = false,
  className: _className = '',
  conformanceText: _conformanceText = null,
  optionsTable: _optionsTable = null,
  label: _label = null,
  labelKey: _labelKey = 'title',
  maxTextLength: _maxTextLength = 2024,
  onFocus: _onFocus = () => null,
  testingIdentifier: _testingIdentifier = '',
  valueKey: _valueKey = 'identifier',
  path
}) => {
  const options = useMemo(() => {
    let reports = ndtReports;
    if (_summaryType === 'maintenance') {
      reports = maintenanceReports;
    }
    return reports.map(report => _objectSpread(_objectSpread({}, report), {}, {
      identifier: report.id,
      title: `${report.reportTypeName} ${report.reportNumber}`
    }));
  }, [ndtReports, maintenanceReports, _summaryType]);

  // The OptionPickerField takes content in the format
  // { id: { ...value } } so transform
  let selected = JSON.parse(JSON.stringify(_value || {}));
  if (selected.id) {
    selected = {
      [selected.id]: selected
    };
  }
  const handleChange = val => {
    if (val === undefined || val.value === null) {
      return onChange({
        value: null,
        path
      });
    }
    const [selection] = Object.values(val.value);
    if (!selection) {
      return onChange(_objectSpread(_objectSpread({}, val), {}, {
        path
      }));
    }
    const {
      title,
      id
    } = selection;
    return onChange(_objectSpread(_objectSpread({}, val), {}, {
      path,
      value: {
        title,
        id
      }
    }));
  };
  useEffect(() => {
    // we need to re-run the filter query when summaryType changes
    // (or when job changes for that matter)
    if (_summaryType === 'maintenance') {
      reportWithMaintenanceActions.serverFilter({
        job: [job],
        ndts: false,
        maintenance: true
      }, maintenanceIndexQuery, true);
    } else if (_summaryType === 'ndt') {
      reportWithNDTActions.serverFilter({
        job: [job],
        ndts: true,
        maintenance: false
      }, ndtIndexQuery, true);
    }
  }, [maintenanceIndexQuery, ndtIndexQuery, _summaryType, reportWithMaintenanceActions, reportWithNDTActions, job]);
  return /*#__PURE__*/React.createElement(UncomposedOptionPickerField, {
    path: path,
    enabled: enabled,
    options: options,
    value: selected || null,
    onChange: handleChange,
    allowsMultipleSelection: _allowsMultipleSelection,
    allowsOther: _allowsOther,
    allowsParentSelection: _allowsParentSelection,
    className: _className,
    conformanceText: _conformanceText,
    optionsTable: _optionsTable,
    label: _label,
    labelKey: _labelKey,
    maxTextLength: _maxTextLength,
    onFocus: _onFocus,
    testingIdentifier: _testingIdentifier,
    valueKey: _valueKey
  });
};
IssueSummaryPicker.propTypes = {
  enabled: PropTypes.bool.isRequired,
  job: PropTypes.shape({
    id: PropTypes.string
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  path: PropTypes.string.isRequired,
  reportWithNDTActions: PropTypes.shape({
    serverFilter: PropTypes.func
  }).isRequired,
  reportWithNDT: PropTypes.shape({
    indexQuery: PropTypes.shape({
      initialized: PropTypes.bool
    }),
    list: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  reportWithMaintenanceActions: PropTypes.shape({
    serverFilter: PropTypes.func
  }).isRequired,
  reportWithMaintenance: PropTypes.shape({
    indexQuery: PropTypes.shape({
      initialized: PropTypes.bool
    }),
    list: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  summaryType: PropTypes.oneOf(['ndt', 'maintenance']),
  allowsMultipleSelection: PropTypes.bool,
  allowsOther: PropTypes.bool,
  allowsParentSelection: PropTypes.bool,
  // defaults are assigned at a lower level
  className: PropTypes.string,
  conformanceText: PropTypes.string,
  label: PropTypes.string,
  labelKey: PropTypes.string,
  maxTextLength: PropTypes.number,
  onFocus: PropTypes.func,
  optionsTable: PropTypes.string,
  testingIdentifier: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.shape({})), PropTypes.shape({}), PropTypes.string]),
  valueKey: PropTypes.string
};
export { IssueSummaryPicker as UncomposedIssueSummaryPicker };
export default entityComposition(IssueSummaryPicker, ['reportWithNDT', 'reportWithMaintenance'], {
  enabled: enabledSelector,
  value: valueSelector,
  job: state => state.report.active.Job,
  // do not use the newPathSelector on this component - it
  // is already done on the OptionPickerField it makes use of.

  conformanceText: conformanceSelector,
  label: labelSelector,
  options: optionsSelector,
  onChange: logValueOnChange,
  path: newPathSelector
});